import { SensorAlertSeverity } from 'common';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Image } from 'tamagui';

import { DashboardCard } from './DashboardCard';
import {
  waterIcon,
  waterLeakIcon,
  humidityAlertIcon,
  waterQualityCompanyLogo,
} from '../../../assets';
import { useHumidityAlertContext } from '../../../contexts/HumidityAlertContext';
import { useWaterAlertContext, WaterAlertState } from '../../../contexts/WaterAlertContext';
import { useWaterQualitySensorStatus } from '../../../hooks';
import { useCachedStatus } from '../../../hooks/status/useCachedStatus';
import { ROUTE_PATHNAMES } from '../../../routes/routeConstants';
import {
  getGlowColors,
  getGradientColors,
  getIconBgColor,
  getIconColor,
  getStatusTextColor,
  Status,
} from '../../../types/Status';

export const WaterQualityCard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: cachedStatus } = useCachedStatus('waterQuality', useWaterQualitySensorStatus);
  const { alertState: waterAlertState } = useWaterAlertContext();
  const {
    hasActiveHumidityAlerts,
    alertSeverity: humidityAlertSeverity,
    humidityAlertCount,
  } = useHumidityAlertContext();

  const { status, quality, statusLabel, humidity, leaks } = cachedStatus || {
    status: Status.Good,
    quality: '',
    statusLabel: 'Good',
    humidity: undefined,
    leaks: 0,
  };

  // Priority: Leak alerts first, then humidity alerts, then normal humidity display
  let middlePrimaryIcon;
  let middlePrimaryContent = humidity;
  let middleSecondaryContent = t('water.humidity');

  const hasActiveWaterAlerts = waterAlertState !== WaterAlertState.NoAlert;

  // Override status based on alert types and severity
  let cardStatus = status;
  if (hasActiveWaterAlerts || leaks > 0) {
    // Leak alerts are always Error (red)
    cardStatus = Status.Error;
  } else if (hasActiveHumidityAlerts) {
    // Humidity alerts: Low/Medium = Poor (orange), High/Critical = Error (red)
    if (
      humidityAlertSeverity === SensorAlertSeverity.Low ||
      humidityAlertSeverity === SensorAlertSeverity.Medium
    ) {
      cardStatus = Status.Poor;
    } else {
      cardStatus = Status.Error;
    }
  }

  if (hasActiveWaterAlerts || leaks > 0) {
    middlePrimaryIcon = <Image src={waterLeakIcon} width={48} height={48} alt="Water Leak" />;
    middlePrimaryContent = `${leaks > 0 ? leaks : 1} ${t('water.leakPresent')}`;
    middleSecondaryContent = t('water.waterLeak');
  } else if (hasActiveHumidityAlerts) {
    middlePrimaryIcon = (
      <Image
        src={humidityAlertIcon}
        width={48}
        height={48}
        tintColor="white"
        alt={t('humidity.highHumidity')}
      />
    );
    middlePrimaryContent = `${humidityAlertCount} ${humidityAlertCount === 1 ? t('common.alert') : t('common.alerts')}`;
    middleSecondaryContent = t('humidity.alert');
  }

  return (
    <DashboardCard
      iconSrc={waterIcon}
      iconBgColor={getIconBgColor(cardStatus)}
      iconColor={getIconColor(cardStatus)}
      brandLogoSrc={waterQualityCompanyLogo}
      middlePrimaryIcon={middlePrimaryIcon}
      middlePrimaryText={middlePrimaryContent}
      middleSecondaryText={middleSecondaryContent}
      bottomPrimaryText={statusLabel}
      bottomPrimaryTextColor={getStatusTextColor(cardStatus)}
      bottomSecondaryText={t([`devices.${quality}`, 'devices.unknown'])}
      backgroundGradientColors={getGradientColors(cardStatus)}
      glowGradientColors={getGlowColors(cardStatus)}
      onPress={() => navigate(ROUTE_PATHNAMES.water)}
    />
  );
};
